import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Chip,
  Checkbox,
  FormControlLabel,
  Breadcrumbs,
  Link as MuiLink,
  InputAdornment,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import TableChartIcon from '@mui/icons-material/TableChart';
import PrintIcon from '@mui/icons-material/Print';
import ClearIcon from '@mui/icons-material/Clear';
import TablePagination from '@mui/material/TablePagination';

interface LoanType {
  loanTypeId: string;
  bank: string;
  acronym: string;
  description: string;
  rateType: string;
  rateTerm: string;
  fixMargin: number;
  irsCapRate: number;
  runningPremiumRate: number;
  tenure: number;
  gracePeriod: number;
  repaymentPeriod: number;
  principalRepaymentFrequency: string;
  interestRepaymentFrequency: string;
  irsHedge: boolean;
  arrangementFee: number;
  status: 'Active' | 'Inactive';
  creationDate: Date;
  lastUpdatedDate: Date;
  createdBy: string;
  lastUpdatedBy: string;
}

const LoanTypeMaintenance: React.FC = () => {
  const [loanTypes, setLoanTypes] = useState<LoanType[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedLoanType, setSelectedLoanType] = useState<LoanType | null>(null);
  const [dialogType, setDialogType] = useState<'add' | 'edit' | 'view' | 'delete'>('add');
  const [searchBank, setSearchBank] = useState('');
  const [searchAcronym, setSearchAcronym] = useState('');
  const [searchDescription, setSearchDescription] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [formData, setFormData] = useState({
    bank: '',
    acronym: '',
    description: '',
    rateType: '',
    rateTerm: '',
    fixMargin: '',
    irsCapRate: '',
    runningPremiumRate: '',
    tenure: '',
    gracePeriod: '',
    repaymentPeriod: '',
    principalRepaymentFrequency: '',
    interestRepaymentFrequency: '',
    irsHedge: false,
    arrangementFee: '',
    status: 'Active',
  });

  const banks = ['Bank A', 'Bank B', 'Bank C']; // TODO: Fetch from API
  const rateTypes = ['Saibor'];
  const rateTerms = ['3 Month', '6 Month', 'Other'];
  const frequencies = ['Monthly', 'Quarterly', 'Semi-Annual', 'Annual'];

  useEffect(() => {
    // TODO: Fetch loan types from API
    const mockLoanTypes: LoanType[] = [
      {
        loanTypeId: 'LT001',
        bank: 'Bank A',
        acronym: 'STL',
        description: 'Short Term Loan',
        rateType: 'Saibor',
        rateTerm: '3 Month',
        fixMargin: 2.5000,
        irsCapRate: 5.0000,
        runningPremiumRate: 1.0000,
        tenure: 12,
        gracePeriod: 3,
        repaymentPeriod: 9,
        principalRepaymentFrequency: 'Monthly',
        interestRepaymentFrequency: 'Monthly',
        irsHedge: true,
        arrangementFee: 1.0000,
        status: 'Active',
        creationDate: new Date('2024-01-01'),
        lastUpdatedDate: new Date('2024-01-01'),
        createdBy: 'Admin',
        lastUpdatedBy: 'Admin',
      },
      {
        loanTypeId: 'LT002',
        bank: 'Bank B',
        acronym: 'LTL',
        description: 'Long Term Loan',
        rateType: 'Saibor',
        rateTerm: '6 Month',
        fixMargin: 3.0000,
        irsCapRate: 5.5000,
        runningPremiumRate: 1.2000,
        tenure: 36,
        gracePeriod: 6,
        repaymentPeriod: 30,
        principalRepaymentFrequency: 'Quarterly',
        interestRepaymentFrequency: 'Quarterly',
        irsHedge: false,
        arrangementFee: 1.5000,
        status: 'Active',
        creationDate: new Date('2024-02-01'),
        lastUpdatedDate: new Date('2024-02-01'),
        createdBy: 'Admin',
        lastUpdatedBy: 'Admin',
      },
      {
        loanTypeId: 'LT003',
        bank: 'Bank C',
        acronym: 'MTL',
        description: 'Medium Term Loan',
        rateType: 'Saibor',
        rateTerm: 'Other',
        fixMargin: 2.8000,
        irsCapRate: 5.2000,
        runningPremiumRate: 1.1000,
        tenure: 24,
        gracePeriod: 4,
        repaymentPeriod: 20,
        principalRepaymentFrequency: 'Semi-Annual',
        interestRepaymentFrequency: 'Semi-Annual',
        irsHedge: true,
        arrangementFee: 1.2000,
        status: 'Inactive',
        creationDate: new Date('2024-03-01'),
        lastUpdatedDate: new Date('2024-03-01'),
        createdBy: 'Admin',
        lastUpdatedBy: 'Admin',
      },
      {
        loanTypeId: 'LT004',
        bank: 'Bank A',
        acronym: 'RCL',
        description: 'Revolving Credit Loan',
        rateType: 'Saibor',
        rateTerm: '3 Month',
        fixMargin: 2.6000,
        irsCapRate: 5.1000,
        runningPremiumRate: 1.0500,
        tenure: 18,
        gracePeriod: 2,
        repaymentPeriod: 16,
        principalRepaymentFrequency: 'Monthly',
        interestRepaymentFrequency: 'Monthly',
        irsHedge: false,
        arrangementFee: 1.1000,
        status: 'Active',
        creationDate: new Date('2024-04-01'),
        lastUpdatedDate: new Date('2024-04-01'),
        createdBy: 'Admin',
        lastUpdatedBy: 'Admin',
      },
      {
        loanTypeId: 'LT005',
        bank: 'Bank B',
        acronym: 'SBL',
        description: 'Syndicated Bank Loan',
        rateType: 'Saibor',
        rateTerm: '6 Month',
        fixMargin: 3.2000,
        irsCapRate: 5.7000,
        runningPremiumRate: 1.3000,
        tenure: 48,
        gracePeriod: 12,
        repaymentPeriod: 36,
        principalRepaymentFrequency: 'Annual',
        interestRepaymentFrequency: 'Annual',
        irsHedge: true,
        arrangementFee: 1.8000,
        status: 'Inactive',
        creationDate: new Date('2024-05-01'),
        lastUpdatedDate: new Date('2024-05-01'),
        createdBy: 'Admin',
        lastUpdatedBy: 'Admin',
      },
      {
        loanTypeId: 'LT006',
        bank: 'Bank C',
        acronym: 'BRL',
        description: 'Bridge Loan',
        rateType: 'Saibor',
        rateTerm: 'Other',
        fixMargin: 2.9000,
        irsCapRate: 5.3000,
        runningPremiumRate: 1.1500,
        tenure: 6,
        gracePeriod: 1,
        repaymentPeriod: 5,
        principalRepaymentFrequency: 'Monthly',
        interestRepaymentFrequency: 'Monthly',
        irsHedge: false,
        arrangementFee: 1.0000,
        status: 'Active',
        creationDate: new Date('2024-06-01'),
        lastUpdatedDate: new Date('2024-06-01'),
        createdBy: 'Admin',
        lastUpdatedBy: 'Admin',
      },
    ];
    setLoanTypes(mockLoanTypes);
  }, []);

  const handleOpenDialog = (type: 'add' | 'edit' | 'view' | 'delete', loanType?: LoanType) => {
    setDialogType(type);
    if (loanType) {
      setSelectedLoanType(loanType);
      setFormData({
        bank: loanType.bank,
        acronym: loanType.acronym,
        description: loanType.description,
        rateType: loanType.rateType,
        rateTerm: loanType.rateTerm,
        fixMargin: loanType.fixMargin.toString(),
        irsCapRate: loanType.irsCapRate.toString(),
        runningPremiumRate: loanType.runningPremiumRate.toString(),
        tenure: loanType.tenure.toString(),
        gracePeriod: loanType.gracePeriod.toString(),
        repaymentPeriod: loanType.repaymentPeriod.toString(),
        principalRepaymentFrequency: loanType.principalRepaymentFrequency,
        interestRepaymentFrequency: loanType.interestRepaymentFrequency,
        irsHedge: loanType.irsHedge,
        arrangementFee: loanType.arrangementFee.toString(),
        status: loanType.status,
      });
    } else {
      setFormData({
        bank: '',
        acronym: '',
        description: '',
        rateType: '',
        rateTerm: '',
        fixMargin: '',
        irsCapRate: '',
        runningPremiumRate: '',
        tenure: '',
        gracePeriod: '',
        repaymentPeriod: '',
        principalRepaymentFrequency: '',
        interestRepaymentFrequency: '',
        irsHedge: false,
        arrangementFee: '',
        status: 'Active',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedLoanType(null);
  };

  const handleSave = () => {
    // TODO: Implement save logic
    handleCloseDialog();
  };

  const handleDelete = () => {
    if (selectedLoanType) {
      // TODO: Implement delete logic with validation
      handleCloseDialog();
    }
  };

  const filteredLoanTypes = loanTypes.filter(loanType => {
    const matchesBank = !searchBank || loanType.bank === searchBank;
    const matchesAcronym = !searchAcronym || 
      loanType.acronym.toLowerCase().includes(searchAcronym.toLowerCase());
    const matchesDescription = !searchDescription || 
      loanType.description.toLowerCase().includes(searchDescription.toLowerCase());
    return matchesBank && matchesAcronym && matchesDescription;
  });

  const paginatedLoanTypes = filteredLoanTypes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/dashboard">
          Home
        </MuiLink>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/parameters">
          Parameters
        </MuiLink>
        <Typography color="text.primary">Loan Type Maintenance</Typography>
      </Breadcrumbs>
      <Typography variant="h4" gutterBottom>
        Loan Type Maintenance
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<TableChartIcon />}
          sx={{
            borderRadius: 2,
            fontWeight: 500,
            bgcolor: '#fff',
            color: '#1E293B',
            borderColor: '#E5E7EB',
            textTransform: 'none',
            boxShadow: 'none',
            px: 2.5,
            '&:hover': { bgcolor: '#F3F4F6', borderColor: '#6366F1' }
          }}
        >
          Export to Excel
        </Button>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          sx={{
            borderRadius: 2,
            fontWeight: 500,
            bgcolor: '#fff',
            color: '#1E293B',
            borderColor: '#E5E7EB',
            textTransform: 'none',
            boxShadow: 'none',
            px: 2.5,
            '&:hover': { bgcolor: '#F3F4F6', borderColor: '#6366F1' }
          }}
        >
          Print
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog('add')}
          color="primary"
          sx={{
            fontWeight: 600,
            px: 4,
            borderRadius: 2,
            boxShadow: '0 1px 2px 0 rgba(59,130,246,0.08)',
            textTransform: 'none',
            '&:hover': { bgcolor: '#2563EB' }
          }}
        >
          Add New Loan Type
        </Button>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          alignItems: 'center',
          gap: 2,
          mb: 3,
          p: 2,
          bgcolor: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderRadius: 3,
          boxShadow: '0 1px 3px 0 rgba(0,0,0,0.03)',
        }}
      >
        <FormControl sx={{ minWidth: 180, maxWidth: 220, bgcolor: '#fff', borderRadius: 2 }} size="small">
          <InputLabel>Bank</InputLabel>
          <Select
            value={searchBank}
            label="Bank"
            onChange={(e) => setSearchBank(e.target.value)}
            sx={{ height: 40 }}
          >
            <MenuItem value="">All Banks</MenuItem>
            {banks.map((bank) => (
              <MenuItem key={bank} value={bank}>{bank}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          placeholder="Acronym"
          value={searchAcronym}
          onChange={(e) => setSearchAcronym(e.target.value)}
          size="small"
          InputProps={{
            sx: { bgcolor: '#fff', borderRadius: 2, height: 40 }
          }}
          sx={{ minWidth: 180, maxWidth: 220 }}
        />
        <TextField
          placeholder="Description"
          value={searchDescription}
          onChange={(e) => setSearchDescription(e.target.value)}
          size="small"
          InputProps={{
            sx: { bgcolor: '#fff', borderRadius: 2, height: 40 }
          }}
          sx={{ minWidth: 240, maxWidth: 320 }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          color="primary"
          startIcon={<SearchIcon />}
          size="medium"
          sx={{ fontWeight: 500, px: 3, borderRadius: 2, boxShadow: 'none', textTransform: 'none', height: 40, minWidth: 100 }}
        >
          Search
        </Button>
        <Button
          variant="contained"
          color="warning"
          startIcon={<ClearIcon />}
          onClick={() => { setSearchBank(''); setSearchAcronym(''); setSearchDescription(''); }}
          size="medium"
          sx={{ fontWeight: 500, px: 3, borderRadius: 2, boxShadow: 'none', textTransform: 'none', height: 40, minWidth: 100 }}
        >
          Reset
        </Button>
      </Box>
      <Paper 
        sx={{ 
          p: 2, 
          mb: 3,
          borderRadius: 3,
          boxShadow: '0 2px 8px 0 rgba(99,102,241,0.06)',
          border: '1px solid #E5E7EB',
          background: '#fff',
          mt: 2,
          overflowX: 'auto'
        }}
      >
        <Table sx={{ minWidth: 1200 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Loan Type ID</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Bank</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Rate Type</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Rate Term</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Fix Margin</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Tenure</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Grace Period</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Repayment Period</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Principal Repayment Frequency</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Interest Repayment Frequency</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedLoanTypes.map((loanType, idx) => (
              <TableRow
                key={loanType.loanTypeId}
                hover
                sx={{
                  '&:hover': { bgcolor: '#F5F6FF' },
                  borderBottom: idx !== paginatedLoanTypes.length - 1 ? '1px solid #E5E7EB' : 'none',
                  transition: 'background 0.2s',
                }}
              >
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{loanType.loanTypeId}</TableCell>
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{loanType.bank}</TableCell>
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{loanType.rateType}</TableCell>
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{loanType.rateTerm}</TableCell>
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{loanType.fixMargin.toFixed(4)}</TableCell>
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{loanType.tenure}</TableCell>
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{loanType.gracePeriod}</TableCell>
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{loanType.repaymentPeriod}</TableCell>
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{loanType.principalRepaymentFrequency}</TableCell>
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{loanType.interestRepaymentFrequency}</TableCell>
                <TableCell sx={{ py: 1 }}>
                  <Chip 
                    label={loanType.status} 
                    size="small"
                    sx={{ 
                      bgcolor: loanType.status === 'Active' ? '#D1FAE5' : '#FEE2E2',
                      color: loanType.status === 'Active' ? '#059669' : '#DC2626',
                      fontWeight: 500
                    }} 
                  />
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <IconButton 
                    size="small" 
                    onClick={() => handleOpenDialog('view', loanType)}
                    sx={{ color: '#6366F1' }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => handleOpenDialog('edit', loanType)}
                    sx={{ color: '#6366F1' }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => handleOpenDialog('delete', loanType)}
                    sx={{ color: '#DC2626' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredLoanTypes.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#374151',
          fontWeight: 600,
          borderBottom: '1px solid #E5E7EB',
          pb: 2
        }}>
          {dialogType === 'add' && 'Add New Loan Type'}
          {dialogType === 'edit' && 'Edit Loan Type'}
          {dialogType === 'view' && 'View Loan Type Details'}
          {dialogType === 'delete' && 'Delete Loan Type'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {dialogType !== 'delete' ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Loan Type ID"
                  value={selectedLoanType?.loanTypeId || 'Auto-generated'}
                  fullWidth
                  disabled
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E5E7EB',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Bank</InputLabel>
                  <Select
                    value={formData.bank}
                    onChange={(e) => setFormData({ ...formData, bank: e.target.value })}
                    label="Bank"
                    disabled={dialogType === 'view'}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#6366F1',
                      },
                    }}
                  >
                    {banks.map((bank) => (
                      <MenuItem key={bank} value={bank}>{bank}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Acronym"
                  value={formData.acronym}
                  onChange={(e) => setFormData({ ...formData, acronym: e.target.value })}
                  fullWidth
                  disabled={dialogType === 'view'}
                  inputProps={{ maxLength: 10 }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E5E7EB',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366F1',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  fullWidth
                  disabled={dialogType === 'view'}
                  inputProps={{ maxLength: 100 }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E5E7EB',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366F1',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ color: '#374151', fontWeight: 600, mb: 1 }}>
                  Interest Basis
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Rate Type</InputLabel>
                  <Select
                    value={formData.rateType}
                    onChange={(e) => setFormData({ ...formData, rateType: e.target.value })}
                    label="Rate Type"
                    disabled={dialogType === 'view'}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#6366F1',
                      },
                    }}
                  >
                    {rateTypes.map((type) => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Rate Term</InputLabel>
                  <Select
                    value={formData.rateTerm}
                    onChange={(e) => setFormData({ ...formData, rateTerm: e.target.value })}
                    label="Rate Term"
                    disabled={dialogType === 'view'}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#6366F1',
                      },
                    }}
                  >
                    {rateTerms.map((term) => (
                      <MenuItem key={term} value={term}>{term}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Fix Margin (%)"
                  type="number"
                  value={formData.fixMargin}
                  onChange={(e) => setFormData({ ...formData, fixMargin: e.target.value })}
                  fullWidth
                  disabled={dialogType === 'view'}
                  inputProps={{ step: "0.0001" }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E5E7EB',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366F1',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="IRS Cap Rate (%)"
                  type="number"
                  value={formData.irsCapRate}
                  onChange={(e) => setFormData({ ...formData, irsCapRate: e.target.value })}
                  fullWidth
                  disabled={dialogType === 'view'}
                  inputProps={{ step: "0.0001" }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E5E7EB',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366F1',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Running Premium Rate (%)"
                  type="number"
                  value={formData.runningPremiumRate}
                  onChange={(e) => setFormData({ ...formData, runningPremiumRate: e.target.value })}
                  fullWidth
                  disabled={dialogType === 'view'}
                  inputProps={{ step: "0.0001" }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E5E7EB',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366F1',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Tenure (months)"
                  type="number"
                  value={formData.tenure}
                  onChange={(e) => setFormData({ ...formData, tenure: e.target.value })}
                  fullWidth
                  disabled={dialogType === 'view'}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E5E7EB',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366F1',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Grace Period (months)"
                  type="number"
                  value={formData.gracePeriod}
                  onChange={(e) => setFormData({ ...formData, gracePeriod: e.target.value })}
                  fullWidth
                  disabled={dialogType === 'view'}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E5E7EB',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366F1',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Repayment Period (months)"
                  type="number"
                  value={formData.repaymentPeriod}
                  onChange={(e) => setFormData({ ...formData, repaymentPeriod: e.target.value })}
                  fullWidth
                  disabled={dialogType === 'view'}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E5E7EB',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366F1',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Principal Repayment Frequency</InputLabel>
                  <Select
                    value={formData.principalRepaymentFrequency}
                    onChange={(e) => setFormData({ ...formData, principalRepaymentFrequency: e.target.value })}
                    label="Principal Repayment Frequency"
                    disabled={dialogType === 'view'}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#6366F1',
                      },
                    }}
                  >
                    {frequencies.map((frequency) => (
                      <MenuItem key={frequency} value={frequency}>{frequency}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Interest Repayment Frequency</InputLabel>
                  <Select
                    value={formData.interestRepaymentFrequency}
                    onChange={(e) => setFormData({ ...formData, interestRepaymentFrequency: e.target.value })}
                    label="Interest Repayment Frequency"
                    disabled={dialogType === 'view'}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#6366F1',
                      },
                    }}
                  >
                    {frequencies.map((frequency) => (
                      <MenuItem key={frequency} value={frequency}>{frequency}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.irsHedge}
                      onChange={(e) => setFormData({ ...formData, irsHedge: e.target.checked })}
                      disabled={dialogType === 'view'}
                      sx={{
                        color: '#6366F1',
                        '&.Mui-checked': {
                          color: '#6366F1',
                        },
                      }}
                    />
                  }
                  label="IRS Hedge"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Arrangement Fee (%)"
                  type="number"
                  value={formData.arrangementFee}
                  onChange={(e) => setFormData({ ...formData, arrangementFee: e.target.value })}
                  fullWidth
                  disabled={dialogType === 'view'}
                  inputProps={{ step: "0.0001" }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E5E7EB',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366F1',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    label="Status"
                    disabled={dialogType === 'view'}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#6366F1',
                      },
                    }}
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {selectedLoanType && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      label="Creation Date"
                      value={selectedLoanType.creationDate.toLocaleDateString()}
                      fullWidth
                      disabled
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E5E7EB',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Last Updated Date"
                      value={selectedLoanType.lastUpdatedDate.toLocaleDateString()}
                      fullWidth
                      disabled
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E5E7EB',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Created By"
                      value={selectedLoanType.createdBy}
                      fullWidth
                      disabled
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E5E7EB',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Last Updated By"
                      value={selectedLoanType.lastUpdatedBy}
                      fullWidth
                      disabled
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E5E7EB',
                        },
                      }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          ) : (
            <Typography sx={{ color: '#374151' }}>
              Are you sure you want to delete this loan type? This action cannot be undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #E5E7EB' }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{
              color: '#374151',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                bgcolor: '#F5F6FF',
              },
            }}
          >
            Cancel
          </Button>
          {dialogType === 'delete' ? (
            <Button 
              onClick={handleDelete} 
              color="error"
              sx={{
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Delete
            </Button>
          ) : (
            <Button 
              onClick={handleSave} 
              variant="contained"
              sx={{
                bgcolor: '#6366F1',
                '&:hover': {
                  bgcolor: '#4F46E5',
                },
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoanTypeMaintenance; 